import type { JSX } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/features/auth/session';
import { useActiveOrganization } from '@/features/organization/active-organization-context';
import { JoinPondokCard } from '@/features/organization/join-pondok-card';
import { FeatureGrid, SOLO_FEATURES } from '@/features/organization/membership-features';
import { useCreatePersonalSpace } from '@/features/personal-space/hooks';
import { ApiError } from '@/lib/api/client';

/**
 * What a registered account that belongs to no organization sees.
 *
 * This used to be a dead end: membership is invitation-only (ADR-027), so
 * an account nobody had invited could only wait. ADR-031 changed that -
 * starting is now unconditional, and a pondok is what you join to gain a
 * community, not a gate you must pass to begin memorizing at all. So this
 * panel leads with the path that needs nobody's permission and presents
 * joining as the second, additive one.
 *
 * Both lists below describe only what this platform has built and running
 * today. What a solo learner gets and what a pondok adds are listed
 * separately and honestly: there is no teacher in a personal space, so
 * evaluation and murajaah scheduling are named as things joining brings,
 * not things quietly implied to already work alone.
 */

export function NoMembershipPanel(): JSX.Element {
  const { refreshUser } = useAuth();
  const { select } = useActiveOrganization();
  const createSpace = useCreatePersonalSpace();

  function handleStart(): void {
    createSpace.mutate(undefined, {
      onSuccess: (space) => {
        // Re-resolve the new membership truthfully via /auth/me before
        // selecting it (ADR-026 Addendum, AO-13/AO-15): `select` only
        // accepts an id the refreshed membership list actually contains,
        // so the response alone is never trusted. Reused verbatim from
        // the invitation-acceptance flow rather than invented here.
        refreshUser()
          .then(() => {
            select(space.organizationId);
          })
          .catch(() => {
            // Best effort: a transient refresh failure must not undo a
            // space that was really created. The next page load resolves
            // the membership normally.
          });
      },
    });
  }

  const errorMessage =
    createSpace.error instanceof ApiError
      ? createSpace.error.message
      : createSpace.error !== null
        ? 'Terjadi kesalahan. Silakan coba lagi.'
        : null;

  return (
    <div className="flex flex-col gap-6">
      <Card className="border-primary/30 bg-primary/5">
        <CardHeader>
          <CardTitle className="text-base">Mulai hafalan Anda sekarang</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 text-sm">
          <p className="leading-relaxed text-muted-foreground">
            Anda tidak perlu menunggu siapa pun untuk mulai. Buat{' '}
            <strong className="text-foreground">ruang hafalan pribadi</strong> - milik Anda sendiri,
            berisi target dan catatan setoran Anda sendiri - lalu mulai hari ini juga.
          </p>
          <div>
            <Button type="button" onClick={handleStart} disabled={createSpace.isPending}>
              {createSpace.isPending ? 'Menyiapkan...' : 'Mulai sendiri'}
            </Button>
          </div>
          {errorMessage !== null && (
            <Alert variant="destructive">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}
          <FeatureGrid features={SOLO_FEATURES} />
        </CardContent>
      </Card>

      <JoinPondokCard />
    </div>
  );
}
